import type { Project } from '../../../types/project';

function getCoating(projectTypes: Project['projectTypes'], type: string, subType: string) {
  if (type === 'fabrication' && subType === 'buckleArrestor') {
    return projectTypes.fabrication?.options?.buckleArrestor?.features?.coating?.enabled;
  }
  if (type === 'fabrication' && subType === 'pipeInPipe') {
    return projectTypes.fabrication?.options?.pipeInPipe?.features?.coating?.enabled;
  }
  if (type === 'fabrication' && subType === 'bulkhead') {
    return projectTypes.fabrication?.options?.bulkhead?.features?.coating?.enabled;
  }
  if (type === 'fabrication' && subType === 'spools') {
    return projectTypes.fabrication?.options?.spools?.features?.coating?.enabled;
  }
  if (type === 'pipeline' && subType === 'none') {
    return projectTypes.pipeline?.features?.coating?.enabled;
  }
  if (type === 'others' && subType === 'none') {
    return projectTypes.others?.features?.coating?.enabled;
  }
  return false;
}

function isEnabledMaterialTracing(projectTypes: Project['projectTypes'], type: string, subType: string) {
  if (type === 'fabrication' && subType === 'buckleArrestor') {
    return projectTypes.fabrication?.options?.buckleArrestor?.features?.materialTraceability?.enabled;
  }
  if (type === 'fabrication' && subType === 'pipeInPipe') {
    return projectTypes.fabrication?.options?.pipeInPipe?.features?.materialTraceability?.enabled;
  }
  if (type === 'fabrication' && subType === 'bulkhead') {
    return projectTypes.fabrication?.options?.bulkhead?.features?.materialTraceability?.enabled;
  }
  if (type === 'fabrication' && subType === 'spools') {
    return projectTypes.fabrication?.options?.spools?.features?.materialTraceability?.enabled;
  }
  if (type === 'pipeline' && subType === 'none') {
    return projectTypes.pipeline?.features?.materialTraceability?.enabled;
  }
  if (type === 'others' && subType === 'none') {
    return projectTypes.others?.features?.materialTraceability?.enabled;
  }
  return false;
}

function isEnabledPipefitter(projectTypes: Project['projectTypes'], type: string, subType: string) {
  if (type === 'fabrication' && subType === 'buckleArrestor') {
    return projectTypes.fabrication?.options?.buckleArrestor?.features?.pipeFitter?.enabled;
  }
  if (type === 'fabrication' && subType === 'pipeInPipe') {
    return projectTypes.fabrication?.options?.pipeInPipe?.features?.pipeFitter?.enabled;
  }
  if (type === 'fabrication' && subType === 'bulkhead') {
    return projectTypes.fabrication?.options?.bulkhead?.features?.pipeFitter?.enabled;
  }
  if (type === 'fabrication' && subType === 'spools') {
    return projectTypes.fabrication?.options?.spools?.features?.pipeFitter?.enabled;
  }
  if (type === 'pipeline' && subType === 'none') {
    return projectTypes.pipeline?.features?.pipeFitter?.enabled;
  }
  if (type === 'others' && subType === 'none') {
    return projectTypes.others?.features?.pipeFitter?.enabled;
  }
  return false;
}

function getPumpHouseEnabled(projectTypes: Project['projectTypes'], type: string, subType: string) {
  if (type === 'pipeline' && subType === 'none') {
    return !!(projectTypes.pipeline?.features?.coating?.machines as { pumpHouse?: { enabled?: boolean } })
      ?.pumpHouse?.enabled;
  }
  return false;
}

function isEnabledWelding(projectTypes: Project['projectTypes'], type: string, subType: string) {
  if (type === 'fabrication' && subType === 'buckleArrestor') {
    return projectTypes.fabrication?.options?.buckleArrestor?.features?.welding?.enabled;
  }
  if (type === 'fabrication' && subType === 'pipeInPipe') {
    return projectTypes.fabrication?.options?.pipeInPipe?.features?.welding?.enabled;
  }
  if (type === 'fabrication' && subType === 'bulkhead') {
    return projectTypes.fabrication?.options?.bulkhead?.features?.welding?.enabled;
  }
  if (type === 'pipeline' && subType === 'none') {
    return projectTypes.pipeline?.features?.welding?.enabled;
  }
  if (type === 'others' && subType === 'none') {
    return projectTypes.others?.features?.welding?.enabled;
  }
  return false;
}

/** Same pipe index order as Material Traceability `pipeSizesData`. */
export function extractPipeSizesFromPipeDetails(
  setupData: Record<string, unknown>,
  project: Project
): Array<Record<string, unknown>> {
  const pipeSizes: Array<Record<string, unknown>> = [];
  const projectTypes = project.projectTypes;

  Object.values(setupData).forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const setup = entry as Record<string, unknown>;
    const projectType = String(setup.projectType ?? '');
    const projectSubType = String(setup.projectSubType ?? 'none');
    const coating = getCoating(projectTypes, projectType, projectSubType);
    const materialTraceability = isEnabledMaterialTracing(projectTypes, projectType, projectSubType);
    const pipeFitter = isEnabledPipefitter(projectTypes, projectType, projectSubType);
    const isWeldingEnabled = isEnabledWelding(projectTypes, projectType, projectSubType);
    const isPumpHouseEnabledFlag = getPumpHouseEnabled(projectTypes, projectType, projectSubType);

    const sizes = setup.pipeSizes;
    if (!Array.isArray(sizes)) return;

    sizes.forEach((pipeEntry) => {
      if (!pipeEntry || typeof pipeEntry !== 'object') return;
      const pipe = pipeEntry as Record<string, unknown>;
      const pipeName = String(pipe.pipeName ?? `Pipe ${pipe.pipeOrder ?? pipe.pipeId ?? ''}`.trim());
      const pipeSizeVal = pipe.pipeSize ?? pipe.size ?? 0;
      const wallThickness = pipe.wallThickness ?? 0;
      const groupId =
        pipe.groupId != null && String(pipe.groupId).trim() !== '' ? String(pipe.groupId).trim() : '';
      const baseDisplayName =
        projectSubType === 'none' && projectType !== 'fabrication'
          ? `${projectType === 'pipeline' ? 'Pipeline' : projectType === 'others' ? 'Others' : projectType}  ${pipeName} (${pipeSizeVal}" ×  ${wallThickness}")`
          : `${projectSubType === 'buckleArrestor' ? 'Buckle Arrestor' :
              projectSubType === 'pipeInPipe' ? 'Pipe in Pipe' :
                projectSubType === 'bulkhead' ? 'Bulk Head' :
                  projectSubType === 'spools' ? 'Spools' :
                    projectSubType}  ${pipeName} (${pipeSizeVal}"  × ${wallThickness}")`;

      pipeSizes.push({
        pipeId: pipe.pipeId,
        pipeName,
        projectSubType,
        projectType,
        projectId: setup.projectId,
        projectTypeId: setup.projectTypeId,
        size: pipeSizeVal,
        wallThickness,
        groupId: groupId || undefined,
        manufacturers: pipe.manufacturers || [],
        coating,
        materialTraceability,
        pipeFitter,
        isWeldingEnabled,
        isPumpHouseEnabled: isPumpHouseEnabledFlag,
        displayName: groupId ? `${baseDisplayName} · ${groupId}` : baseDisplayName,
      });
    });
  });

  return pipeSizes;
}
